import { socialImageSrc } from "@shared/socialImages";
import { useEffect } from "react";
import { formatSeoDescription, formatSeoTitle } from "@shared/seoFormat";

interface SeoHeadProps {
  title: string;
  description: string;
  path: string;
  type?: "website" | "article";
  image?: string;
  publishedDate?: string;
  updatedDate?: string;
  /** @deprecated Schemas are emitted as static JSON-LD in the HTML source. This prop is kept for backward compatibility but ignored. */
  structuredData?: object | object[];
  noindex?: boolean;
  keywords?: string;
}

const SITE_NAME = "Nordiska Uterum";
const DOMAIN = "https://nordiskauterum.se";
const DEFAULT_OG_IMAGE = "/manus-storage/og-page-hem_694a00fc.png";

function setMeta(name: string, content: string, isProperty = false) {
  const attr = isProperty ? "property" : "name";
  let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.content = content;
}

function setLink(rel: string, href: string, attrs?: Record<string, string>) {
  const selector = attrs
    ? `link[rel="${rel}"]${Object.entries(attrs)
        .map(([k, v]) => `[${k}="${v}"]`)
        .join("")}`
    : `link[rel="${rel}"]`;
  let el = document.querySelector(selector) as HTMLLinkElement;
  if (!el) {
    el = document.createElement("link");
    el.rel = rel;
    if (attrs) Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
    document.head.appendChild(el);
  }
  el.href = href;
}

export default function SeoHead({
  title,
  description,
  path,
  type = "website",
  image,
  publishedDate,
  updatedDate,
  noindex = false,
  keywords,
}: SeoHeadProps) {
  useEffect(() => {
    let cancelled = false;
    const apply = (entry?: import("@shared/seoData").PageSeo) => {
      if (cancelled) return;
      const fullTitle = entry?.title || formatSeoTitle(title);
      const fullDescription =
        entry?.description || formatSeoDescription(description);
      const fullUrl = entry?.canonical || `${DOMAIN}${path}`;
      const ogType = entry?.ogType || type;
      const ogImage = new URL(
        socialImageSrc(entry?.ogImage || image || DEFAULT_OG_IMAGE),
        DOMAIN
      ).href;
      document.title = fullTitle;
      document.documentElement.lang = "sv";
      setMeta("description", fullDescription);
      setMeta(
        "robots",
        noindex || path === "/tack/"
          ? "noindex, nofollow"
          : "index, follow, max-image-preview:large"
      );
      setMeta("keywords", entry?.keywords || keywords || "");
      setMeta("og:title", fullTitle, true);
      setMeta("og:description", fullDescription, true);
      setMeta("og:type", ogType, true);
      setMeta("og:url", fullUrl, true);
      setMeta("og:site_name", SITE_NAME, true);
      setMeta("og:locale", "sv_SE", true);
      setMeta("og:image", ogImage, true);
      setMeta("og:image:width", "1200", true);
      setMeta("og:image:height", "630", true);
      setMeta("og:image:alt", fullTitle, true);
      setMeta("twitter:card", "summary_large_image");
      setMeta("twitter:title", fullTitle);
      setMeta("twitter:description", fullDescription);
      setMeta("twitter:image", ogImage);
      setLink("canonical", fullUrl);
      // Clear route-specific data before replacing it; stale article/review data must not survive navigation.
      document
        .querySelectorAll(
          'meta[property="article:published_time"],meta[property="article:modified_time"],script[type="application/ld+json"]'
        )
        .forEach(el => el.remove());
      for (const schema of entry?.schemas || []) {
        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.textContent = JSON.stringify(schema);
        document.head.appendChild(script);
        const article = schema as Record<string, unknown>;
        if (article["@type"] === "Article") {
          if (article.datePublished)
            setMeta(
              "article:published_time",
              String(article.datePublished),
              true
            );
          if (article.dateModified)
            setMeta(
              "article:modified_time",
              String(article.dateModified),
              true
            );
        }
      }
    };
    // Generated from the same registry as prerendered HTML. Vite gives this data a versioned chunk URL.
    import("@shared/seoData.json")
      .then(module => {
        const map = module.default as Record<
          string,
          import("@shared/seoData").PageSeo
        >;
        apply(map[path]);
      })
      .catch(() => apply());
    return () => {
      cancelled = true;
    };
  }, [
    title,
    description,
    path,
    type,
    image,
    publishedDate,
    updatedDate,
    noindex,
    keywords,
  ]);
  return null;
}
